import { CarrierType } from '@yard/app/api'
import { OcuppancyWithFilterData } from '@yard/stores/OccupancyStore'

// L2.4 — Slot hover tooltip support.
//
// Pure helpers for deriving the planner-facing breakdown of a container's
// journey from the raw OcuppancyWithFilterData payload. Kept out of the
// React component so the inference can be unit-tested without setting up
// MUI/Tolgee providers.

export type ContainerDirection = 'import' | 'export' | 'tranship'

/**
 * Classify a container's movement direction from its inbound/outbound carrier
 * types. The contract follows standard TOS terminology:
 *
 *   - Vessel → Vessel = Transshipment (T-shipment)
 *   - Vessel → Truck/Train = Import (arrived by sea, leaving by land)
 *   - Truck/Train → Vessel = Export (arrived by land, leaving by sea)
 *   - Anything else (Truck → Truck, no carrier info, etc.) = undefined
 *     so the caller can hide the direction row rather than show a misleading
 *     label. Yard-internal moves don't have a meaningful import/export label.
 *
 * `Universal` is treated as "not a vessel" — it's a placeholder for unknown
 * carrier mode and shouldn't be claimed as transshipment.
 */
export const inferDirection = (
  inbound: CarrierType | null | undefined,
  outbound: CarrierType | null | undefined,
): ContainerDirection | undefined => {
  const inIsVessel = inbound === CarrierType.Vessel
  const outIsVessel = outbound === CarrierType.Vessel
  if (inIsVessel && outIsVessel) return 'tranship'
  if (inIsVessel) return 'import'
  if (outIsVessel) return 'export'
  return undefined
}

export interface ContainerCargoFlags {
  reefer: boolean
  dangerous: boolean
  outOfGauge: boolean
  empty: boolean
}

/**
 * Collapse the inbound/outbound split flags into a single "is true on either
 * leg" view. The planner cares about the slot's current characteristics for
 * hazard/handling purposes, and a container is functionally e.g. dangerous if
 * it was dangerous on arrival OR will be dangerous on departure.
 *
 * `isReefer` is a single boolean on the container itself (no leg split).
 */
export const collectCargoFlags = (occ: OcuppancyWithFilterData): ContainerCargoFlags => ({
  reefer: occ.isReefer === true,
  dangerous: occ.inboundIsDangerous === true || occ.outboundIsDangerous === true,
  outOfGauge: occ.inboundIsOOG === true || occ.outboundIsOOG === true,
  empty: occ.inboundIsEmpty === true || occ.outboundIsEmpty === true,
})

/** True iff at least one cargo flag is on — caller hides the chip row otherwise. */
export const hasAnyCargoFlag = (flags: ContainerCargoFlags): boolean =>
  flags.reefer || flags.dangerous || flags.outOfGauge || flags.empty
